import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Inbox, MessageSquare, AtSign, Star, Archive, Trash2, Send, RefreshCw,
  User, Users, Filter, Search, ChevronRight, MoreHorizontal, ExternalLink,
  Facebook, Instagram, Twitter, Linkedin, Globe, Clock, CheckCircle, AlertCircle,
  UserPlus, Ticket, StickyNote, ArrowRight, Tag, Flag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  google_business: Globe
};

const PLATFORM_COLORS = {
  facebook: 'bg-blue-600',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  twitter: 'bg-sky-500',
  linkedin: 'bg-blue-700',
  google_business: 'bg-blue-500'
};

const STATUS_COLORS = {
  new: 'bg-blue-500',
  open: 'bg-yellow-500',
  pending: 'bg-orange-500',
  resolved: 'bg-green-500',
  spam: 'bg-gray-500'
};

const PRIORITY_COLORS = {
  low: 'bg-gray-400',
  medium: 'bg-blue-400',
  high: 'bg-orange-400',
  urgent: 'bg-red-500'
};

const SENTIMENT_COLORS = {
  positive: 'text-green-600',
  neutral: 'text-gray-500',
  negative: 'text-red-600'
};

const SocialInbox = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [interactions, setInteractions] = useState([]);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Reply state
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  
  // Dialogs
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  const fetchInteractions = useCallback(async () => {
    try {
      let url = `${API_URL}/api/social-mgmt/inbox?token=${token}&limit=50`;
      
      if (statusFilter) url += `&status=${statusFilter}`;
      if (platformFilter) url += `&platform=${platformFilter}`;
      if (activeTab === 'mine') url += `&assigned_to=me`;
      if (activeTab === 'unassigned') url += `&assigned_to=unassigned`;
      
      const res = await fetch(url);
      const data = await res.json();
      
      if (data.success) {
        setInteractions(data.interactions || []);
      }
    } catch (error) {
      console.error('Error fetching interactions:', error);
    }
  }, [token, statusFilter, platformFilter, activeTab]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/stats?token=${token}`);
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users?token=${token}`);
      const data = await res.json();
      if (data.users) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchInteractionDetails = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/${id}?token=${token}`);
      const data = await res.json();
      if (data.success) {
        setSelectedInteraction(data.interaction);
      }
    } catch (error) {
      console.error('Error fetching interaction details:', error);
    }
  }, [token]);

  // Progressive — each tile paints as it arrives.
  useEffect(() => {
    setLoading(true);
    const tasks = [fetchInteractions(), fetchStats(), fetchUsers()]
      .map((p) => Promise.resolve(p).catch((e) => {
        console.error('[SocialInbox] tile failed:', e); return null;
      }));
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  }, [fetchInteractions, fetchStats, fetchUsers]);

  useEffect(() => {
    fetchInteractions();
  }, [statusFilter, platformFilter, activeTab, fetchInteractions]);

  const handleSelectInteraction = (interaction) => {
    fetchInteractionDetails(interaction.id);
  };

  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedInteraction) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/${selectedInteraction.id}/reply?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: replyContent })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Reply sent');
        setReplyContent('');
        fetchInteractionDetails(selectedInteraction.id);
      } else {
        toast.error(data.message || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Error sending reply');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedInteraction) return;
    
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/${selectedInteraction.id}?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Status updated to ${status}`);
        fetchInteractionDetails(selectedInteraction.id);
        fetchInteractions();
        fetchStats();
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const handleAssign = async (userId) => {
    if (!selectedInteraction) return;
    
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/${selectedInteraction.id}/assign?token=${token}&assigned_to=${userId}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Interaction assigned');
        setShowAssignDialog(false);
        fetchInteractionDetails(selectedInteraction.id);
        fetchInteractions();
      }
    } catch (error) {
      toast.error('Error assigning interaction');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !selectedInteraction) return;
    
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/${selectedInteraction.id}/notes?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: noteContent })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Note added');
        setNoteContent('');
        setShowNoteDialog(false);
        fetchInteractionDetails(selectedInteraction.id);
      }
    } catch (error) {
      toast.error('Error adding note');
    }
  };

  const handleCreateLead = async () => {
    if (!selectedInteraction) return;
    
    try {
      const res = await fetch(`${API_URL}/api/social-mgmt/inbox/${selectedInteraction.id}/create-lead?token=${token}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Lead created successfully');
        fetchInteractionDetails(selectedInteraction.id);
      } else {
        toast.error(data.detail || 'Failed to create lead');
      }
    } catch (error) {
      toast.error('Error creating lead');
    }
  };

  const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
    const Icon = PLATFORM_ICONS[platform] || Globe;
    return <Icon className={className} />;
  };

  const filteredInteractions = interactions.filter(i => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      i.author_name?.toLowerCase().includes(query) ||
      i.content?.toLowerCase().includes(query) ||
      i.author_username?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="social-inbox">
      {/* Header */}
      <div className="p-4 border-b bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">Social Inbox</h1>
            <p className="text-sm text-slate-500">Manage comments, messages, and mentions</p>
          </div>
          <Button variant="outline" onClick={() => { fetchInteractions(); fetchStats(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            <span>Open: {stats?.total_open || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
            <span>Unassigned: {stats?.unassigned || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
            <span>My Items: {stats?.my_assigned || 0}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - List */}
        <div className="w-1/3 border-r flex flex-col bg-slate-50 dark:bg-slate-800">
          {/* Filters */}
          <div className="p-3 border-b space-y-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="mine">Mine</TabsTrigger>
                <TabsTrigger value="unassigned">Unassigned</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-24 h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Interaction List */}
          <ScrollArea className="flex-1">
            {filteredInteractions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No interactions found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredInteractions.map((interaction) => (
                  <div
                    key={interaction.id}
                    onClick={() => handleSelectInteraction(interaction)}
                    className={`p-3 cursor-pointer hover:bg-white dark:hover:bg-slate-700 transition-colors ${
                      selectedInteraction?.id === interaction.id ? 'bg-white dark:bg-slate-700 border-l-2 border-accent' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={interaction.author_avatar_url} />
                        <AvatarFallback>
                          {interaction.author_name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm truncate">
                            {interaction.author_name || 'Unknown'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {interaction.created_at && formatDistanceToNow(new Date(interaction.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 mt-0.5">
                          {interaction.content}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${PLATFORM_COLORS[interaction.platform]}`}>
                            <PlatformIcon platform={interaction.platform} className="w-3 h-3 text-white" />
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {interaction.interaction_type}
                          </Badge>
                          <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[interaction.status]}`}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right Panel - Detail */}
        <div className="flex-1 flex flex-col bg-white dark:bg-slate-900">
          {selectedInteraction ? (
            <>
              {/* Detail Header */}
              <div className="p-4 border-b">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={selectedInteraction.author_avatar_url} />
                      <AvatarFallback>
                        {selectedInteraction.author_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{selectedInteraction.author_name}</span>
                        <a 
                          href={selectedInteraction.author_profile_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-accent"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                      <span className="text-sm text-slate-500">@{selectedInteraction.author_username}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Select value={selectedInteraction.status} onValueChange={handleUpdateStatus}>
                      <SelectTrigger className="w-28 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="spam">Spam</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => setShowAssignDialog(true)}>
                      <User className="w-4 h-4 mr-1" />
                      Assign
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowNoteDialog(true)}>
                      <StickyNote className="w-4 h-4 mr-1" />
                      Note
                    </Button>
                  </div>
                </div>

                {/* CRM Link */}
                <div className="mt-3 flex items-center gap-3">
                  {selectedInteraction.crm_contact ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/contacts/${selectedInteraction.crm_contact.id}`)}
                    >
                      <User className="w-4 h-4 mr-1" />
                      View Contact: {selectedInteraction.crm_contact.name}
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleCreateLead}>
                      <UserPlus className="w-4 h-4 mr-1" />
                      Create Lead
                    </Button>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${PLATFORM_COLORS[selectedInteraction.platform]}`}>
                      <PlatformIcon platform={selectedInteraction.platform} className="w-3 h-3 text-white" />
                    </div>
                    <span className="capitalize">{selectedInteraction.platform}</span>
                    <span>•</span>
                    <span className="capitalize">{selectedInteraction.interaction_type}</span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {/* Original Message */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={selectedInteraction.author_avatar_url} />
                      <AvatarFallback>{selectedInteraction.author_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm">{selectedInteraction.author_name}</span>
                    <span className="text-xs text-slate-500">
                      {selectedInteraction.created_at && format(new Date(selectedInteraction.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  <div className="ml-10 bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                    <p className="whitespace-pre-wrap">{selectedInteraction.content}</p>
                  </div>
                </div>

                {/* Replies */}
                {selectedInteraction.replies?.map((reply) => (
                  <div key={reply.id} className="mb-4">
                    <div className="flex items-center gap-2 mb-2 justify-end">
                      <span className="text-xs text-slate-500">
                        {reply.sent_at && format(new Date(reply.sent_at), 'MMM d, h:mm a')}
                      </span>
                      <span className="font-medium text-sm">You</span>
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-accent text-white">
                          {user?.name?.charAt(0) || 'Y'}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="ml-10 mr-10 bg-accent/10 rounded-lg p-3 text-right">
                      <p className="whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  </div>
                ))}

                {/* Internal Notes */}
                {selectedInteraction.internal_notes?.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <StickyNote className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium">Internal Notes</span>
                    </div>
                    {selectedInteraction.internal_notes.map((note) => (
                      <div key={note.id} className="mb-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <p className="text-sm">{note.content}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {note.author_name} • {note.created_at && format(new Date(note.created_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Reply Box */}
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <div className="text-xs text-slate-500">
                    Replying via {selectedInteraction.platform}
                  </div>
                  <Button onClick={handleSendReply} disabled={sending || !replyContent.trim()}>
                    {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Reply
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm">Choose an interaction from the list to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Interaction</DialogTitle>
            <DialogDescription>Select a team member to handle this interaction</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => handleAssign(u.id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <Avatar className="w-10 h-10">
                  <AvatarFallback>{u.full_name?.charAt(0) || u.email?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="font-medium">{u.full_name || u.email}</p>
                  <p className="text-sm text-slate-500">{u.role}</p>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Internal Note</DialogTitle>
            <DialogDescription>This note is only visible to your team</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Write your note..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={!noteContent.trim()}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialInbox;
